import { useState, useRef } from "react";
import { supabase } from "../supabase";

const AVATARS = ["🦁", "🐯", "🐻", "🦊", "🐼", "🐸", "🐧", "🦋", "🐬", "🦄"];
const TASK_EMOJIS = ["⭐", "📚", "🧹", "🐾", "🏃", "🍽️", "🛁", "🎨", "🎵", "💪"];
const REWARD_EMOJIS = ["🎮", "🍕", "🎬", "🛍️", "🎉", "🍦", "🎢", "📱", "🎁", "🏖️"];

const STEPS = ["welcome", "child", "task", "reward", "share", "done"];

export default function OnboardingFlow({ parent, onComplete }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [childName, setChildName] = useState("");
  const [childAvatar, setChildAvatar] = useState(AVATARS[0]);
  const [childId, setChildId] = useState(null);
  const childIdRef = useRef(null);

  const [taskTitle, setTaskTitle] = useState("");
  const [taskEmoji, setTaskEmoji] = useState(TASK_EMOJIS[0]);
  const [taskPoints, setTaskPoints] = useState(10);

  const [rewardTitle, setRewardTitle] = useState("");
  const [rewardEmoji, setRewardEmoji] = useState(REWARD_EMOJIS[0]);
  const [rewardPoints, setRewardPoints] = useState(50);

  const familyCode = parent?.family_code || "—";
  const parentName = parent?.name?.split(" ")[0] || "there";

  const next = () => setStep((s) => s + 1);
  const err = (msg) => { setError(msg); setLoading(false); };

  async function handleAddChild() {
    if (!childName.trim()) return err("Give your child a name!");
    setLoading(true); setError("");
    console.log("Adding child for parent:", parent);
    const { data, error: e } = await supabase
      .from("children")
      .insert({ parent_id: parent.id, name: childName.trim(), avatar: childAvatar, age: 0, pin: '0000' })
      .select()
      .single();
    if (e) { console.error("Child insert error:", JSON.stringify(e)); return err("Couldn't add child. Try again."); }
    setChildId(data.id);
    childIdRef.current = data.id;
    setLoading(false);
    next();
  }

  async function handleAddTask() {
    if (!taskTitle.trim()) return err("Give the task a title!");
    if (!childIdRef.current) return err("Something went wrong. Refresh and try again.");
    setLoading(true); setError("");
    const { error: e } = await supabase.from("tasks").insert({
      child_id: childIdRef.current,
      parent_id: parent.id,
      name: taskTitle.trim(),
      emoji: taskEmoji,
      points: Number(taskPoints),
      frequency: 'Daily',
      status: 'active',
    });
    if (e) {
      console.error("Task insert error:", JSON.stringify(e));
      return err(`DB error: ${e.message || e.code || JSON.stringify(e)}`);
    }
    setLoading(false); next();
  }

  async function handleAddReward() {
    if (!rewardTitle.trim()) return err("Give the reward a title!");
    setLoading(true); setError("");
    const { error: e } = await supabase.from("rewards").insert({
      child_id: childIdRef.current, parent_id: parent.id,
      name: rewardTitle.trim(), emoji: rewardEmoji,
      points: Number(rewardPoints),
    });
    if (e) {
      console.error("Reward insert error:", JSON.stringify(e));
      return err(`DB error: ${e.message || e.code || JSON.stringify(e)}`);
    }
    setLoading(false); next();
  }

  async function handleFinish() {
    setLoading(true);
    await supabase.from("parents").update({ onboarding_complete: true }).eq("id", parent.id);
    setLoading(false);
    onComplete();
  }

  const progressPct = (step / (STEPS.length - 1)) * 100;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {step < STEPS.length - 1 && (
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
          </div>
        )}

        {step === 0 && (
          <StepWrap emoji="👋" title={`Hey ${parentName}!`}>
            <p style={styles.body}>Let's get ThriveTrack set up for your family in about 2 minutes. You'll add a child, create their first mission and reward — and you're done!</p>
            <Btn onClick={next}>Let's go →</Btn>
          </StepWrap>
        )}

        {step === 1 && (
          <StepWrap emoji={childAvatar} title="Add your first child">
            <label style={styles.label}>Their name</label>
            <input style={styles.input} value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="e.g. Jaylen" autoFocus />
            <label style={styles.label}>Pick an avatar</label>
            <div style={styles.emojiGrid}>
              {AVATARS.map((a) => (
                <button key={a} style={{ ...styles.emojiBtn, ...(childAvatar === a ? styles.emojiBtnActive : {}) }} onClick={() => setChildAvatar(a)}>{a}</button>
              ))}
            </div>
            {error && <p style={styles.error}>{error}</p>}
            <Btn onClick={handleAddChild} loading={loading}>Add child →</Btn>
          </StepWrap>
        )}

        {step === 2 && (
          <StepWrap emoji={taskEmoji} title={`${childName}'s first mission`}>
            <label style={styles.label}>Mission name</label>
            <input style={styles.input} value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="e.g. Clean your room" autoFocus />
            <label style={styles.label}>Pick an emoji</label>
            <div style={styles.emojiGrid}>
              {TASK_EMOJIS.map((e) => (
                <button key={e} style={{ ...styles.emojiBtn, ...(taskEmoji === e ? styles.emojiBtnActive : {}) }} onClick={() => setTaskEmoji(e)}>{e}</button>
              ))}
            </div>
            <label style={styles.label}>Coins earned</label>
            <input style={{ ...styles.input, width: 100 }} type="number" min={1} value={taskPoints} onChange={(e) => setTaskPoints(e.target.value)} />
            {error && <p style={styles.error}>{error}</p>}
            <Btn onClick={handleAddTask} loading={loading}>Save mission →</Btn>
          </StepWrap>
        )}

        {step === 3 && (
          <StepWrap emoji={rewardEmoji} title="Create a reward">
            <p style={{ ...styles.body, marginBottom: 16 }}>What does {childName} get to earn? Set a reward and how many coins it costs.</p>
            <label style={styles.label}>Reward name</label>
            <input style={styles.input} value={rewardTitle} onChange={(e) => setRewardTitle(e.target.value)} placeholder="e.g. Movie night" autoFocus />
            <label style={styles.label}>Pick an emoji</label>
            <div style={styles.emojiGrid}>
              {REWARD_EMOJIS.map((e) => (
                <button key={e} style={{ ...styles.emojiBtn, ...(rewardEmoji === e ? styles.emojiBtnActive : {}) }} onClick={() => setRewardEmoji(e)}>{e}</button>
              ))}
            </div>
            <label style={styles.label}>Coins required</label>
            <input style={{ ...styles.input, width: 100 }} type="number" min={1} value={rewardPoints} onChange={(e) => setRewardPoints(e.target.value)} />
            {error && <p style={styles.error}>{error}</p>}
            <Btn onClick={handleAddReward} loading={loading}>Save reward →</Btn>
          </StepWrap>
        )}

        {step === 4 && (
          <StepWrap emoji="🔗" title="Your family code">
            <p style={styles.body}>Share this code with your child so they can log in on their device.</p>
            <div style={styles.codeBox}>
              <span style={styles.codeText}>{familyCode}</span>
            </div>
            <CopyBtn text={familyCode} />
            <p style={{ ...styles.body, marginTop: 16, fontSize: 13, color: "#888" }}>They'll find it on the kid login screen under "Enter family code."</p>
            <Btn onClick={next} style={{ marginTop: 8 }}>Next →</Btn>
          </StepWrap>
        )}

        {step === 5 && (
          <StepWrap emoji="🎉" title="You're all set!">
            <p style={styles.body}>{childName} is ready to start earning. Head to your dashboard to manage missions, rewards, and more.</p>
            <div style={styles.summaryBox}>
              <SummaryRow label="Child added" value={`${childAvatar} ${childName}`} />
              <SummaryRow label="First mission" value={`${taskEmoji} ${taskTitle}`} />
              <SummaryRow label="First reward" value={`${rewardEmoji} ${rewardTitle}`} />
            </div>
            <Btn onClick={handleFinish} loading={loading}>Go to dashboard →</Btn>
          </StepWrap>
        )}
      </div>
    </div>
  );
}

function StepWrap({ emoji, title, children }) {
  return (
    <div style={styles.stepWrap}>
      <div style={styles.stepEmoji}>{emoji}</div>
      <h2 style={styles.title}>{title}</h2>
      {children}
    </div>
  );
}

function Btn({ onClick, loading, children, style }) {
  return (
    <button style={{ ...styles.btn, ...style }} onClick={onClick} disabled={loading}>
      {loading ? "Saving…" : children}
    </button>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return <button style={styles.copyBtn} onClick={copy}>{copied ? "✓ Copied!" : "Copy code"}</button>;
}

function SummaryRow({ label, value }) {
  return (
    <div style={styles.summaryRow}>
      <span style={styles.summaryLabel}>{label}</span>
      <span style={styles.summaryValue}>{value}</span>
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  card: { background: "#fff", borderRadius: 20, width: "100%", maxWidth: 440, boxShadow: "0 24px 60px rgba(0,0,0,0.18)", overflow: "hidden" },
  progressTrack: { height: 4, background: "#f0f0f0" },
  progressFill: { height: "100%", background: "linear-gradient(90deg, #6c63ff, #a78bfa)", transition: "width 0.4s ease" },
  stepWrap: { padding: "32px 28px 28px" },
  stepEmoji: { fontSize: 48, marginBottom: 12, display: "block" },
  title: { fontSize: 22, fontWeight: 700, color: "#1a1a2e", marginBottom: 16, lineHeight: 1.2 },
  body: { fontSize: 15, color: "#555", lineHeight: 1.6, marginBottom: 8 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, marginTop: 16 },
  input: { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e0e0e0", fontSize: 15, outline: "none", boxSizing: "border-box" },
  emojiGrid: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 },
  emojiBtn: { fontSize: 22, padding: "6px 10px", borderRadius: 10, border: "2px solid transparent", background: "#f5f5f5", cursor: "pointer" },
  emojiBtnActive: { border: "2px solid #6c63ff", background: "#ede9ff" },
  btn: { marginTop: 24, width: "100%", padding: "13px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #6c63ff, #a78bfa)", color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer" },
  copyBtn: { marginTop: 12, padding: "9px 20px", borderRadius: 10, border: "1.5px solid #6c63ff", background: "#fff", color: "#6c63ff", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  codeBox: { background: "#f5f0ff", border: "2px dashed #a78bfa", borderRadius: 14, padding: "18px 24px", textAlign: "center", marginTop: 12 },
  codeText: { fontSize: 28, fontWeight: 800, letterSpacing: 6, color: "#6c63ff" },
  error: { color: "#e53e3e", fontSize: 13, marginTop: 8 },
  summaryBox: { background: "#f8f8ff", borderRadius: 12, padding: "14px 16px", marginBottom: 8, marginTop: 8 },
  summaryRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #eee" },
  summaryLabel: { fontSize: 13, color: "#888" },
  summaryValue: { fontSize: 14, fontWeight: 600, color: "#333" },
};
